<script>
export const pageName = 'rockets-flight-view';
export const pageRoute = '/rockets/{rocketId}/flights/{flightId}';
export const title = 'View Flight'
export const requireMembership = true;

import { getCurrentUserId } from '../../js/auth.js';
import { getRocketView, getUserView } from '../../js/readModel.js';
import { attemptTypeToFullAttempt, statusToStatus, recordTypeToEnglish, motorsToString } from '../../../functions/domain/certification-utils.js';
import { getMapTasks } from '../../js/utils/map.js';

export default {
	data() {
		return { 
			user: null,
			rocket: null,
			flight: null,
			ready: false
		}
	},

	methods: {
		attemptTypeToFullAttempt,
		statusToStatus,
		recordTypeToEnglish,
		motorsToString,
		async show(opts) {

			this.user = await getUserView(getCurrentUserId());
			this.rocket = await getRocketView(getCurrentUserId(), opts.rocketId)
			this.flight = this.rocket.getFlight(opts.flightId);


			this.flightLogs = this.flight.logs.map(l => ({
				id: l.id,
				name: l.name,
				size: l.size,
				remove: false
			}))
			
			this.mapTasks = await getMapTasks(this.flight.mapTasks);

			this.ready = true;

		}
	}
}

</script>
<template>
	<div class="container py-5" v-if="ready">
		<div class="row justify-content-center">
			<div class="col-12 col-md-10 col-xl-8 my-5">
				<h1 class="display-4 text-center mb-3">View Flight</h1>
				<p class="text-muted text-center mb-5">View the details of your flight.</p>
					
				<div v-if="rocket.photoUrl" class="mb-4">
					<img :src="rocket.photoUrl" :alt="`Photo of ${rocket.name}`" class="w-100" />
					<hr />
				</div>
				
				<div class="row">
					<div class="col-12 col-md-6">
						<div class="card">
							<div class="card-header">
								<h4 class="card-header-title">Flight details</h4>
							</div>
							<div class="card-body">
								<div class="list-group list-group-flush">
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Description</span>
											</div>
											<div class="col-auto">
												{{ this.flight.name }}
											</div>
										</div>
									</div>
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Flown On</span>
											</div>
											<div class="col-auto">
												{{ this.flight.flownOn.format('yyyy-MM-dd HH:mm') }}
											</div>
										</div>
									</div>
									
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Location</span>
											</div>
											<div class="col-auto">
												{{ this.flight.location }}
											</div>
										</div>
									</div>
									
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Motor(s)</span>
											</div>
											<div class="col-auto">
												{{ motorsToString(this.flight.motors) }} - {{ this.flight.ejections }}
											</div>
										</div>
									</div>
									
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Mass</span>
											</div>
											<div class="col-auto">
												{{ this.flight.mass }}g
											</div>
										</div>
									</div>
									
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Stability Margin</span>
											</div>
											<div class="col-auto">
												{{ this.flight.sm }}
											</div>
										</div>
									</div>
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Outcome</span>
											</div>
											<div class="col-auto">
												{{ this.flight.outcome }}
											</div>
										</div>
									</div>
									<div class="list-group-item">
										<div class="row align-items-center">
											<div class="col">
												<span>Notes</span>
											</div>
											<div class="col-auto">
												{{ this.flight.notes }}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col-12 col-md-6">
						<div class="card">
							<div class="card-header">
								<h4 class="card-header-title">Launch location</h4>
							</div>
							<div class="card-body">
								{{ this.flight.location || 'Unknown' }}
								<div>
									{{ this.flight.latitude ? (this.flight.latitude + ', ' + this.flight.longitude) : 'Unknown coordinates' }}
								</div>
							</div>
						</div>
						<div class="card">
							<div class="card-header">
								<h4 class="card-header-title">Landing location</h4>
							</div>
							<div class="card-body">
								{{ this.flight.landingLatitude ? (this.flight.landingLatitude + ', ' + this.flight.landingLongitude) : 'Unknown coordinates' }}
							</div>
						</div>
						<div class="card">
							<div class="card-header">
								<h4 class="card-header-title">Flight logs</h4>
							</div>
							<div class="card-body py-0">
								<div class="list-group list-group-flush">
									<div v-for="log of flightLogs" class="list-group-item">
										<div class="row align-items-center">
											<div class="col ms-n2">
												<h4 class="fw-normal mb-1">
													{{ log.name }}
												</h4>
											</div>
											<div class="col-auto">
												View
											</div>
										</div>
									</div>
									<div v-if="!flightLogs.length" class="list-group-item">
										No logs
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Model Achievement Programme</h4>
					</div>
					<div class="card-body">
						<p v-if="!flight.mapFlight" class="mb-0">This was not a MAP flight.</p>
						<div v-else>
							<div class="row align-items-center">
								<div class="col">
									<div v-for="task of mapTasks" class="d-inline-block" :aria-title="task.name" :style="`background: url(${task.bg}); background-size: contain; width: 53px; height: 56px;`">
										<img :src="task.icon" style="width: 53px; height: 56px;" />
									</div>
								</div>
								<div class="col-auto">
									<div class="img-container">
										<a :href="flight.flightCardUrl" target="_blank"><img :src="flight.flightCardUrl" /></a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				
				
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Certification</h4>
					</div>
					<div class="card-body">
						<p v-if="!flight.certFlight" class="mb-0">This was not a certification flight.</p>
						<div v-else class="list-group list-group-flush my-n4">
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Flight type
									</div>
									<div class="col-auto">
										{{ attemptTypeToFullAttempt(this.flight.certType) }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Status
									</div>
									<div class="col-auto">
										{{ statusToStatus(this.flight.certStatus) }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Feedback
									</div>
									<div class="col-auto">
										{{ this.flight.certReason }}
									</div>
								</div>
							</div>
							<div v-if="flight.flightCardUrl" class="list-group-item">
								<a :href="flight.flightCardUrl" target="_blank"><img :src="flight.flightCardUrl" class="w-100" /></a>
							</div>
						</div>
					</div>
				</div>		

				
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Altitude record</h4>
					</div>
					<div class="card-body">
						<p v-if="!flight.recordAttempt" class="mb-0">This was not a record attempt flight.</p>
						<div v-else class="list-group list-group-flush my-n4">
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Team name
									</div>
									<div class="col-auto">
										{{ this.flight.teamName || 'N/A' }}
									</div>
								</div>
							</div>
							
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Record Type
									</div>
									<div class="col-auto">
										{{ recordTypeToEnglish(this.flight.recordType) }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Record Class
									</div>
									<div class="col-auto">
										{{ this.flight.recordClass }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Altitude
									</div>
									<div class="col-auto">
										{{ this.flight.reachedAlt }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Altimeter
									</div>
									<div class="col-auto">
										{{ this.flight.altimeter }}
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row align-items-center">
									<div class="col">
										Witness
									</div>
									<div class="col-auto">
										{{ this.flight.witness }}
									</div>
								</div>
							</div>
							<div v-if="flight.flightCardUrl" class="list-group-item">
								<a :href="flight.flightCardUrl" target="_blank"><img :src="flight.flightCardUrl" class="w-100" /></a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
	
</template>
